import {
  Award,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Mic,
  Palette,
  Pin,
  Shield,
  Swords,
  Users,
} from "lucide-react";

import {
  BB_LETTERS,
  BB_LETTER_LABEL,
  BB_LETTER_SPEC_TITLE,
  BB_LETTER_TO_SECTION,
  type BbLetter,
  type SectionId,
} from "@/lib/bbSectionMap";

export interface BrandBibleSectionMeta {
  id: SectionId;
  letter: BbLetter;
  title: string;
  hint: string;
  icon: typeof BookOpen;
}

const SECTION_PRESENTATION: Record<
  BbLetter,
  Pick<BrandBibleSectionMeta, "hint" | "icon">
> = {
  A: { hint: "Who the business is", icon: BookOpen },
  B: { hint: "How it sounds", icon: Mic },
  C: { hint: "What it sells", icon: Pin },
  D: { hint: "Trust signals", icon: Award },
  E: { hint: "Who it serves", icon: Users },
  F: { hint: "Internal only", icon: Swords },
  G: { hint: "What to avoid", icon: Shield },
  H: { hint: "How it looks", icon: Palette },
  I: { hint: "Where the voice runs", icon: Megaphone },
};

/** The existing A-I map remains the only source of letter-to-section truth. */
export const BRAND_BIBLE_SECTIONS: BrandBibleSectionMeta[] = BB_LETTERS.map(
  (letter) => ({
    id: BB_LETTER_TO_SECTION[letter],
    letter,
    title: BB_LETTER_SPEC_TITLE[letter],
    ...SECTION_PRESENTATION[letter],
  }),
);

export const BRAND_BIBLE_SECTION_BY_ID: Record<
  SectionId,
  BrandBibleSectionMeta
> = Object.fromEntries(
  BRAND_BIBLE_SECTIONS.map((section) => [section.id, section]),
) as Record<SectionId, BrandBibleSectionMeta>;

export function BrandBibleSectionPager({
  active,
  onSelect,
}: {
  active: BbLetter;
  onSelect: (letter: BbLetter) => void;
}) {
  const index = BB_LETTERS.indexOf(active);
  const previous = index > 0 ? BB_LETTERS[index - 1] : null;
  const next = index < BB_LETTERS.length - 1 ? BB_LETTERS[index + 1] : null;

  return (
    <nav
      aria-label="Brand Bible section pagination"
      className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-4"
    >
      <button
        type="button"
        aria-label={
          previous
            ? `Previous section · ${previous} · ${BB_LETTER_LABEL[previous]}`
            : "Previous section"
        }
        disabled={!previous}
        onClick={() => previous && onSelect(previous)}
        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-[13px] font-medium text-foreground hover:bg-warm-bg disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft className="size-3.5" aria-hidden="true" />
        {previous ? `${previous} · ${BB_LETTER_LABEL[previous]}` : "Previous"}
      </button>
      <button
        type="button"
        aria-label={
          next
            ? `Next section · ${next} · ${BB_LETTER_LABEL[next]}`
            : "Next section"
        }
        disabled={!next}
        onClick={() => next && onSelect(next)}
        className="inline-flex items-center gap-2 rounded-lg border border-coral bg-coral px-3 py-2 text-[13px] font-semibold text-white hover:bg-coral-hover disabled:cursor-not-allowed disabled:border-border disabled:bg-card disabled:text-muted-foreground disabled:opacity-60"
      >
        {next ? `${next} · ${BB_LETTER_LABEL[next]}` : "Next"}
        <ChevronRight className="size-3.5" aria-hidden="true" />
      </button>
    </nav>
  );
}
